import { getMessagingBootstrap, SalesforceEmbeddedServiceBootstrap } from './embeddedMessagingSession';
import { removeEmbeddedMessagingStyles } from './embeddedMessagingStyles';

/**
 * Tear down Salesforce Enhanced Web Chat (MIAW) or Agentforce Messaging on logout or unmount.
 * Clears the messaging session, then removes the injected widget via the v2 util API when available.
 * @see https://developer.salesforce.com/docs/service/messaging-web/references/m4w-reference/utilAPI.html#removeallcomponents
 * @see https://developer.salesforce.com/docs/service/messaging-web/references/m4w-reference/userVerificationAPI.html#clearsession
 */
export const INIT_SCRIPT_ID = 'salesforce-embedded-init-global';
export const BOOTSTRAP_SCRIPT_ID = 'salesforce-embedded-bootstrap-global';

/**
 * Selectors for the widget DOM that Salesforce embedded messaging injects at the top level of the
 * page. Used as a fallback when `removeAllComponents` is unavailable (e.g. Agentforce Messaging).
 */
const INJECTED_WIDGET_SELECTORS = [
  'embeddedservice-chat-bootstrap',
  'agentforce-messaging-bootstrap',
  '[id^="embeddedMessaging"]',
  '[id^="embedded-messaging"]',
];

/** Time to wait after teardown so in-flight SDK timers/WebSocket handlers can drain. */
export const TEARDOWN_QUIESCENCE_MS = 1500;

let teardownInFlight: Promise<void> | null = null;

const quiesceAfterTeardown = (): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, TEARDOWN_QUIESCENCE_MS);
  });

/**
 * Resolves once any in-flight teardown started by `cleanupEmbeddedChat` has finished. The init path
 * awaits this so a fast navigate-away / navigate-back cannot re-bootstrap while the SDK is still
 * clearing its session.
 */
export function waitForEmbeddedChatTeardown(): Promise<void> {
  return teardownInFlight ?? Promise.resolve();
}

/**
 * Removes any embedded-messaging DOM the SDK injected. Safe to call when nothing is present. Must run
 * after `clearSession` resolves — removing the iframe mid-session can re-trigger socket errors.
 */
function removeInjectedWidgetDom(): void {
  if (typeof document === 'undefined') {
    return;
  }
  INJECTED_WIDGET_SELECTORS.forEach((selector) => {
    try {
      document.querySelectorAll(selector).forEach((el) => el.remove());
    } catch (err) {
      console.warn('removeInjectedWidgetDom failed for selector', selector, err);
    }
  });
}

/**
 * Calls the v2 `removeAllComponents` util API when present. Agentforce Messaging may expose the
 * method but throw "not implemented" — in that case we fall back to manual DOM removal.
 */
function removeMessagingComponents(boot: SalesforceEmbeddedServiceBootstrap): void {
  const removeAll = boot.utilAPI?.removeAllComponents;
  if (!removeAll) {
    removeInjectedWidgetDom();
    return;
  }

  try {
    removeAll();
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();
    const isKnownLimitation =
      msg.includes('not implemented') || msg.includes('removeallcomponents');
    if (!isKnownLimitation) {
      console.warn('removeAllComponents failed; falling back to manual DOM removal', err);
    }
    removeInjectedWidgetDom();
  }
}

/**
 * Wipes the globals this app owns so the next visit can call `init()` from a clean slate.
 *
 * The SDK's own globals (`embeddedservice_bootstrap` / `agentforce_messaging`) are deliberately
 * left alone here: the bundled SDK reads them as bare identifiers, so deleting the binding makes
 * every SDK callback that outlives teardown throw `ReferenceError: agentforce_messaging is not
 * defined`. Re-bootstrap overwrites them in place; see `resetMessagingBootstrapGlobals` for a
 * manual fallback only.
 */
function clearAppGlobals(): void {
  if (window.__embeddedChatMarkReady) {
    window.removeEventListener('onEmbeddedMessagingReady', window.__embeddedChatMarkReady);
    delete window.__embeddedChatMarkReady;
  }
  delete window.initEmbeddedMessaging;
  delete window.__embeddedMessagingReady;
  delete window.__embeddedChatInitialized;
}

/**
 * Drops the SDK bootstrap globals so a freshly loaded bootstrap.min.js can define its own.
 * Fallback only — normal re-bootstrap overwrites globals in place to avoid ReferenceErrors from
 * surviving SDK callbacks. Never call during teardown; see `clearAppGlobals`.
 */
export function resetMessagingBootstrapGlobals(): void {
  delete window.embeddedservice_bootstrap;
  delete window.agentforce_messaging;
}

function removeInjectedScripts(): void {
  document.getElementById(INIT_SCRIPT_ID)?.remove();
  document.getElementById(BOOTSTRAP_SCRIPT_ID)?.remove();
  removeEmbeddedMessagingStyles();
}

/**
 * Clears the messaging session and removes the chat UI (button, window). Supports both Enhanced Web
 * Chat v2 (MIAW) and Agentforce Messaging. Safe to call if messaging was never initialized.
 */
export function teardownSalesforceEmbeddedMessaging(): Promise<void> {
  const boot = getMessagingBootstrap();
  const clear = boot?.userVerificationAPI?.clearSession;

  const finish = () => {
    if (boot) {
      removeMessagingComponents(boot);
    } else {
      removeInjectedWidgetDom();
    }
    clearAppGlobals();
    removeInjectedScripts();
  };

  if (!clear) {
    finish();
    return quiesceAfterTeardown();
  }

  try {
    const result = clear(true);
    if (result && typeof (result as PromiseLike<void>).then === 'function') {
      return Promise.resolve(result)
        .then(finish, (err: unknown) => {
          console.warn('clearSession failed; continuing teardown', err);
          finish();
        })
        .then(quiesceAfterTeardown);
    }
    finish();
    return quiesceAfterTeardown();
  } catch (err: unknown) {
    console.warn('clearSession failed; continuing teardown', err);
    finish();
    return quiesceAfterTeardown();
  }
}

/**
 * Full cleanup for logout: tears down Salesforce messaging, removes injected scripts, and resets
 * all global state.
 */
export function cleanupEmbeddedChat(): Promise<void> {
  if (teardownInFlight) {
    return teardownInFlight;
  }

  teardownInFlight = teardownSalesforceEmbeddedMessaging().finally(() => {
    teardownInFlight = null;
  });

  return teardownInFlight;
}
